/**
 * User-dashboard RichTextEditor wrapper
 *
 * This wraps the ui-components RichTextEditor and adds user-dashboard specific
 * functionality like the EntitySelector in the toolbar.
 */
import Sentry from "@/integrations/Sentry";
import type { IntegrationConnection, Skill } from "@cohesive/api-client";
import {
  RichTextEditor as BaseRichTextEditor,
  type RichTextEditorProps as BaseRichTextEditorProps,
  EditorToolbar,
  Entity,
  getEntityData,
  getEntityName,
  getEntityType,
  INSERT_CONNECTION_COMMAND,
} from "@cohesive/ui-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import React from "react";
import toast from "react-hot-toast";
import { EntitySelector } from "../EntitySelector/EntitySelector";

export interface RichTextEditorProps
  extends Omit<BaseRichTextEditorProps, "renderToolbar" | "onError"> {
  skills?: Skill[];
  integrationConnections?: IntegrationConnection[];
}

/**
 * Internal component that has access to Lexical context for entity insertion
 */
const EntitySelectorWrapper: React.FC<{
  connection: IntegrationConnection;
  onClose: () => void;
  onBack: () => void;
}> = ({ connection, onClose, onBack }) => {
  const [editor] = useLexicalComposerContext();

  const handleEntitySelect = (entity: Entity) => {
    try {
      const entityData = getEntityData(entity);
      const platform = entity.platform;
      const type = getEntityType(entity);
      const id = entityData?.id?.toString() || "";
      const name = getEntityName(entity);

      editor.dispatchCommand(INSERT_CONNECTION_COMMAND, {
        platform,
        type,
        identifier: id,
        title: name,
      });

      onClose();
    } catch (error) {
      console.error("Failed to parse entity identifier:", error);
      toast.error("Invalid entity format");
    }
  };

  return (
    <EntitySelector
      connection={connection}
      onClose={onClose}
      onSelect={handleEntitySelect}
      onBack={onBack}
      backText="Connections"
    />
  );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = React.memo(
  ({
    skills = [],
    integrationConnections = [],
    enableSkills = false,
    enableEntities = false,
    enablePlaceholders = false,
    showToolbar = true,
    ...props
  }) => {
    return (
      <BaseRichTextEditor
        {...props}
        skills={skills}
        integrationConnections={integrationConnections}
        enableSkills={enableSkills}
        enableEntities={enableEntities}
        enablePlaceholders={enablePlaceholders}
        showToolbar={showToolbar}
        onError={(error) => Sentry.captureException(error)}
        renderToolbar={() => (
          <EditorToolbar
            skills={skills}
            integrationConnections={integrationConnections}
            enableSkills={enableSkills}
            enableEntities={enableEntities}
            enablePlaceholders={enablePlaceholders}
            renderEntitySelector={({ connection, onClose, onBack }) => (
              <EntitySelectorWrapper
                connection={connection}
                onClose={onClose}
                onBack={onBack}
              />
            )}
          />
        )}
      />
    );
  },
);
